import React, { useContext, useEffect, useState, useMemo } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import { AuthContext } from "../context/AuthContext";

import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
  Chip,
  Stack,
} from "@mui/material";
import CommonTable from "../components/CommonTable";

const PURPOSE_LABELS = {
  saleable: "For student sales",
  teacher_set: "Teacher set (reference copy)",
};

const InventoryTransfer = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [branches, setBranches] = useState([]);
  const [sourceBranch, setSourceBranch] = useState("");
  const [destBranch, setDestBranch] = useState("");
  const [sourceInventory, setSourceInventory] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemEntry, setItemEntry] = useState(null);
  const [entryPurpose, setEntryPurpose] = useState("saleable");
  const [open, setOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const { user } = useContext(AuthContext);

  const sourceIsCentral = useMemo(
    () => branches.find((b) => b._id === sourceBranch)?.code === "CENTRAL",
    [branches, sourceBranch]
  );

  const maxForPurpose = (inv, purpose) => {
    const saleable = inv.saleableQuantity ?? inv.quantity ?? 0;
    const teacherSet = inv.teacherSetQuantity ?? 0;
    if (purpose === "teacher_set" && !sourceIsCentral) return teacherSet;
    return saleable;
  };

  useEffect(() => {
    const fetchBranches = async () => {
      const res = await axios.get(`/branches?orgId=${organization}`);
      setBranches(res.data);
    };
    if (organization) fetchBranches();
  }, [organization]);

  useEffect(() => {
    if (organization && academicYear) fetchTransfers();
  }, [organization, academicYear, open]);

  useEffect(() => {
    const fetchInventory = async () => {
      const res = await axios.get(
        `/inventory/balance?organization=${organization}&academicYear=${academicYear}&branch=${sourceBranch}`
      );
      setSourceInventory(res.data || []);
      setSelectedItems([]);
    };
    if (organization && academicYear && sourceBranch) fetchInventory();
  }, [organization, academicYear, sourceBranch]);

  const fetchTransfers = async () => {
    const res = await axios.get(
      `/inventory?organization=${organization}&academicYear=${academicYear}`
    );
    setTransfers((res.data || []).filter((tx) => tx.source === "transfer"));
  };

  const groupedTransfers = useMemo(() => {
    const map = {};
    transfers.forEach((tx) => {
      const key = tx.sourceRef || "unknown";
      if (!map[key]) map[key] = [];
      map[key].push(tx);
    });
    return map;
  }, [transfers]);

  const branchName = (id) => branches.find((b) => b._id === id)?.name || id;

  const itemsOptions = sourceInventory
    .filter((inv) => (inv.saleableQuantity ?? inv.quantity) > 0 || (inv.teacherSetQuantity ?? 0) > 0)
    .map((inv) => ({
      id: inv.item,
      label: inv.itemName + (inv.itemModel === "Collection" ? " [Set]" : ""),
      itemModel: inv.itemModel,
      saleableQuantity: inv.saleableQuantity ?? inv.quantity ?? 0,
      teacherSetQuantity: inv.teacherSetQuantity ?? 0,
    }));

  const lineKey = (item) => `${item.id}:${item.itemModel}:${item.purpose}`;

  const handleAddItem = () => {
    if (!itemEntry) return;

    const maxQty = maxForPurpose(itemEntry, entryPurpose);
    if (maxQty <= 0) {
      showMessage(
        entryPurpose === "teacher_set"
          ? "No teacher set stock available at source for this item"
          : "No saleable stock available at source for this item",
        "warning"
      );
      return;
    }

    const qty = Math.min(itemEntry.quantity || 1, maxQty);
    if (qty > maxQty) {
      showMessage(`Quantity exceeds max available (${maxQty})`, "warning");
      return;
    }

    const newItem = { ...itemEntry, quantity: qty, purpose: entryPurpose };
    if (selectedItems.some((it) => lineKey(it) === lineKey(newItem))) {
      showMessage("This item with the same purpose is already added", "info");
      return;
    }

    setSelectedItems([...selectedItems, newItem]);
    setItemEntry(null);
    setEntryPurpose("saleable");
  };

  const handleSubmit = async () => {
    if (!sourceBranch || !destBranch) {
      showMessage(
        "Both source and destination branches are required",
        "warning"
      );
      return;
    }
    if (sourceBranch === destBranch) {
      showMessage(
        "Source and destination branches cannot be the same",
        "warning"
      );
      return;
    }
    if (selectedItems.length === 0) {
      showMessage("Add at least one item to transfer", "warning");
      return;
    }
    if (
      user.role === "branch_manager" &&
      !user.branches.includes(sourceBranch)
    ) {
      showMessage("You can only transfer from your assigned branches", "error");
      return;
    }

    for (const it of selectedItems) {
      const inv = sourceInventory.find(
        (row) => row.item === it.id && row.itemModel === it.itemModel
      );
      const maxQty = inv ? maxForPurpose(inv, it.purpose) : 0;
      if (it.quantity > maxQty) {
        showMessage(`"${it.label}" exceeds available quantity`, "error");
        return;
      }
    }

    const confirmed = await confirm({
      title: "Confirm Transfer",
      message: "Are you sure you want to initiate this transfer?",
    });
    if (!confirmed) return;

    try {
      await axios.post("/inventory/transfer", {
        organization,
        academicYear,
        fromBranch: sourceBranch,
        toBranch: destBranch,
        items: selectedItems.map((it) => ({
          item: it.id,
          itemModel: it.itemModel,
          quantity: it.quantity,
          purpose: it.purpose || "saleable",
        })),
      });
      showMessage("Transfer successfully submitted", "success");
      setOpen(false);
      setSourceBranch("");
      setDestBranch("");
      setSelectedItems([]);
      fetchTransfers();
    } catch (err) {
      showMessage(err.response?.data?.msg || "Transfer failed", "error");
    }
  };

  const getVisibleBranches = () => {
    return user.role === "branch_manager"
      ? branches.filter((b) => user.branches.includes(b._id))
      : branches;
  };

  const entryMaxQty = itemEntry
    ? maxForPurpose(itemEntry, entryPurpose)
    : undefined;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Inventory Transfer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Stock received at Central Office (CO) is transferred to branches. Mark
        copies as <strong>Teacher set</strong> for branch reference — they are
        tracked separately and excluded from student sales availability.
      </Typography>
      <Button variant="contained" onClick={() => setOpen(true)} sx={{ mb: 2 }}>
        Initiate Transfer
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Inventory Transfer</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>From Branch</InputLabel>
            <Select
              value={sourceBranch}
              onChange={(e) => setSourceBranch(e.target.value)}
              label="From Branch"
            >
              {getVisibleBranches()?.map((b) => (
                <MenuItem key={b._id} value={b._id}>
                  {b.name}
                  {b.code === "CENTRAL" ? " (CO)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>To Branch</InputLabel>
            <Select
              value={destBranch}
              onChange={(e) => setDestBranch(e.target.value)}
              label="To Branch"
            >
              {branches
                .filter((b) => b._id !== sourceBranch)
                .map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Typography sx={{ mt: 2, mb: 1 }}>Add Item / Collection</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              options={itemsOptions.filter(
                (opt) =>
                  !selectedItems.some(
                    (sel) =>
                      sel.id === opt.id &&
                      sel.itemModel === opt.itemModel &&
                      sel.purpose === entryPurpose
                  )
              )}
              getOptionLabel={(opt) =>
                `${opt.label} (saleable: ${opt.saleableQuantity}, teacher: ${opt.teacherSetQuantity})`
              }
              value={itemEntry}
              onChange={(e, v) =>
                setItemEntry(v ? { ...v, quantity: 1 } : null)
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Item / Collection" />
              )}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Quantity"
              type="number"
              value={itemEntry?.quantity || 1}
              onChange={(e) => {
                const q = Math.max(1, parseInt(e.target.value, 10) || 1);
                setItemEntry((entry) =>
                  entry ? { ...entry, quantity: q } : entry
                );
              }}
              disabled={!itemEntry}
              sx={{ width: 100 }}
              inputProps={{ min: 1, max: entryMaxQty || undefined }}
            />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Purpose</InputLabel>
              <Select
                value={entryPurpose}
                label="Purpose"
                onChange={(e) => setEntryPurpose(e.target.value)}
              >
                <MenuItem value="saleable">For student sales</MenuItem>
                <MenuItem value="teacher_set">Teacher set</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={handleAddItem}
              disabled={!itemEntry || !entryMaxQty}
            >
              Add
            </Button>
          </Box>

          <Typography sx={{ mt: 2, mb: 1 }}>Selected Items</Typography>
          {selectedItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No items added
            </Typography>
          ) : (
            <Stack spacing={1}>
              {selectedItems.map((item, idx) => {
                const inv = sourceInventory.find(
                  (row) =>
                    row.item === item.id && row.itemModel === item.itemModel
                );
                const maxQty = inv ? maxForPurpose(inv, item.purpose) : 0;
                return (
                  <Box
                    key={lineKey(item)}
                    sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={item.label}
                      color={
                        item.purpose === "teacher_set" ? "warning" : "default"
                      }
                    />
                    <Chip
                      size="small"
                      label={PURPOSE_LABELS[item.purpose] || item.purpose}
                      variant="outlined"
                    />
                    <TextField
                      label="Qty"
                      type="number"
                      size="small"
                      value={item.quantity}
                      onChange={(e) => {
                        let quantity = Math.max(
                          1,
                          parseInt(e.target.value, 10) || 1
                        );
                        if (quantity > maxQty) quantity = maxQty;
                        setSelectedItems((list) =>
                          list.map((it, i) =>
                            i === idx ? { ...it, quantity } : it
                          )
                        );
                      }}
                      sx={{ width: 80 }}
                      inputProps={{ min: 1, max: maxQty }}
                      helperText={`Max: ${maxQty}`}
                    />
                    <Button
                      color="error"
                      size="small"
                      onClick={() =>
                        setSelectedItems((list) =>
                          list.filter((_, i) => i !== idx)
                        )
                      }
                    >
                      Remove
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !sourceBranch || !destBranch || selectedItems.length === 0
            }
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      <CommonTable
        rows={Object.entries(groupedTransfers).map(([ref, txns]) => {
          const outTxn = txns.find((t) => t.type === "OUT");
          const inTxn = txns.find((t) => t.type === "IN");
          return {
            ref,
            date: outTxn?.timestamp || inTxn?.timestamp,
            from: branchName(outTxn?.branch),
            to: branchName(inTxn?.branch),
            items: txns
              .filter((t) => t.type === "IN")
              .map((t) => {
                const purposeTag =
                  t.stockPurpose === "teacher_set" ? " [Teacher set]" : "";
                return `${t.itemModel === "Collection" ? "[Set] " : ""}${
                  t?.item?.name || t.item
                } (${t.quantity})${purposeTag}`;
              })
              .join("\n"),
          };
        })}
        columns={[
          {
            label: "Date",
            renderCell: (row) =>
              row.date ? new Date(row.date).toLocaleString() : "-",
          },
          { label: "From", field: "from" },
          { label: "To", field: "to" },
          {
            label: "Items (received at branch)",
            renderCell: (row) => (
              <Box sx={{ whiteSpace: "pre-line" }}>{row.items}</Box>
            ),
          },
        ]}
      />
    </Box>
  );
};

export default InventoryTransfer;
